import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';

const LOGO_URL = "/logo.svg";




const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

export default function CookiePolicyPage() {
  return (
    <div data-testid="cookie-policy-page">
      <Navbar />
      <SEOHead title="Cookie Policy" description="ChannelLoyalty cookie policy. How we use cookies and tracking technologies on our B2B channel loyalty platform." canonical="https://channelloyalty.ai/cookie-policy" />
      
      <main>
        <section className="section pt-32">
          <div className="container">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Cookie Policy</h1>
              
              <div className="prose prose-lg max-w-none text-gray-600 space-y-6">
                <p>
                  This Cookie Policy explains how ChannelLoyalty ("ChannelLoyalty", "we", "us", or "our") uses cookies and similar technologies when you access or use our website, applications, and digital platforms (collectively, the "Services"). By continuing to use the Services, you consent to the use of cookies in accordance with this Cookie Policy, unless you choose to disable them through your browser settings.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">1. What Are Cookies?</h2>
                <p>
                  Cookies are small text files placed on your device when you visit a website. They are widely used to enable website functionality, enhance user experience, analyze usage patterns, and support security features. Cookies may be session-based (deleted when you close your browser) or persistent (stored on your device for a defined period).
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">2. Types of Cookies We Use</h2>
                <p>ChannelLoyalty uses the following categories of cookies:</p>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">a. Strictly Necessary Cookies</h3>
                <p>These cookies are essential for the operation of the Services and cannot be disabled without affecting core functionality. They are used to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Enable secure access and navigation</li>
                  <li>Maintain session integrity</li>
                  <li>Support authentication and fraud prevention mechanisms</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">b. Performance & Analytics Cookies</h3>
                <p>These cookies help us understand how users interact with our Services by collecting aggregated and anonymized usage information. They are used to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Measure website traffic and performance</li>
                  <li>Identify errors or technical issues</li>
                  <li>Improve platform usability and experience</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">c. Functional Cookies</h3>
                <p>These cookies enable enhanced functionality and personalization. They may be used to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Remember user preferences</li>
                  <li>Support language or regional settings</li>
                  <li>Improve continuity across sessions</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mt-6">d. Security Cookies</h3>
                <p>
                  These cookies are deployed to detect suspicious activity, prevent abuse, and protect users and systems from unauthorized access.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">3. Third-Party Cookies</h2>
                <p>
                  Certain cookies may be placed by third-party service providers engaged by ChannelLoyalty for analytics, security, or performance optimization. Such third parties process data in accordance with their own privacy policies and are contractually required to maintain appropriate safeguards. ChannelLoyalty does not control third-party cookies and disclaims responsibility for their independent practices.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">4. Purpose of Cookie Usage</h2>
                <p>Cookies are used by ChannelLoyalty solely for the following purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Ensuring secure and reliable operation of the Services</li>
                  <li>Enhancing user experience and functionality</li>
                  <li>Analyzing usage trends to improve performance</li>
                  <li>Supporting compliance, auditability, and risk management</li>
                </ul>
                <p>Cookies are not used to collect sensitive personal data unless expressly required for security or compliance purposes.</p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">5. Managing Cookies</h2>
                <p>Most web browsers allow you to manage, block, or delete cookies through their settings. Please note:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Disabling certain cookies may impact the availability or functionality of the Services</li>
                  <li>Strictly necessary cookies cannot be fully disabled through consent mechanisms</li>
                  <li>Browser-specific instructions are available in your browser's help section</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">6. Data Protection & Privacy</h2>
                <p>Information collected through cookies is processed in accordance with ChannelLoyalty's Privacy Policy and Security & Compliance framework. Where applicable, cookie-related data is:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access-controlled</li>
                  <li>Retained only for as long as necessary</li>
                  <li>Processed for legitimate business and compliance purposes</li>
                </ul>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">7. Changes to This Cookie Policy</h2>
                <p>
                  ChannelLoyalty may update this Cookie Policy from time to time to reflect changes in technology, legal requirements, or business practices. Updated versions will be published on the website with the revised effective date.
                </p>

                <h2 className="text-2xl font-bold text-gray-900 mt-8">8. Contact Information</h2>
                <p>For questions or concerns regarding this Cookie Policy, please contact:</p>
                <p><strong>Email:</strong> <a href="mailto:chandra@channelloyalty.ai" className="text-[#2C39B8] hover:underline">chandra@channelloyalty.ai</a></p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <MegaFooter />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
